import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ClipboardList, Loader2 } from 'lucide-react';

const RaciMatrix = () => {
  const [tasks, setTasks] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [tasksRes, subtasksRes] = await Promise.all([
          axios.get('/api/masters/raci/taskmaster'),
          axios.get('/api/masters/raci/subtaskmaster'),
        ]);
        setTasks(tasksRes.data || []);
        setSubtasks(subtasksRes.data || []);
      } catch (e) {
        console.error('RACI fetch error:', e);
        toast.error('Failed to load RACI matrix data.');
        setTasks([]);
        setSubtasks([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getTaskId = (t) => t.task_id ?? t.Task_ID;
  const getTaskNo = (t) => t.task_no ?? t.Task_No;
  const getTaskDesc = (t) => t.task_description ?? t.Task_Description;
  const getSubtaskTaskId = (s) => s.task_id ?? s.Task_ID;
  const getSubtaskNo = (s) => s.subtask_no ?? s.Subtask_No;
  const getSubtaskDesc = (s) => s.subtask_description ?? s.Subtask_Description;
  // Stakeholder RACI from DB columns "ECSBC Cell", "SDA", "BEE" — support different key casings from API
  const getStakeholderRaci = (sub, colKey) => {
    const keys = [colKey, colKey.toLowerCase(), colKey.replace(/\s+/g, '_'), colKey.replace(/\s+/g, '')];
    for (const k of keys) {
      if (sub[k] != null && sub[k] !== '') return String(sub[k]).trim();
    }
    return '';
  };

  const subtaskSortKey = (no) => {
    if (no == null) return [0, ''];
    const s = String(no).trim();
    const numMatch = s.match(/^\d+/);
    const num = numMatch ? parseInt(numMatch[0], 10) : 0;
    const rest = numMatch ? s.slice(numMatch[0].length) : s;
    return [num, rest];
  };

  const rows = [];
  tasks.forEach((task) => {
    const taskSubtasks = subtasks
      .filter((s) => getSubtaskTaskId(s) === getTaskId(task))
      .sort((a, b) => {
        const [na, ra] = subtaskSortKey(getSubtaskNo(a));
        const [nb, rb] = subtaskSortKey(getSubtaskNo(b));
        if (na !== nb) return na - nb;
        return String(ra).localeCompare(String(rb), undefined, { sensitivity: 'base' });
      });
    taskSubtasks.forEach((sub, idx) => {
      rows.push({
        isFirstInTask: idx === 0,
        taskRowSpan: taskSubtasks.length,
        taskDesc: getTaskDesc(task),
        taskNo: getTaskNo(task),
        subtaskNo: getSubtaskNo(sub),
        subtaskDesc: getSubtaskDesc(sub),
        ecsbcCell: getStakeholderRaci(sub, 'ECSBC Cell'),
        sda: getStakeholderRaci(sub, 'SDA'),
        bee: getStakeholderRaci(sub, 'BEE'),
      });
    });
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
      </div>
    );
  }

  // Stakeholder columns: cohesive cool palette (sky, violet, teal) — narrow width, center-aligned
  const STAKEHOLDERS = [
    { key: 'ecsbcCell', label: 'ECSBC Cell', bg: 'bg-sky-100', cellBg: 'bg-sky-50/90', text: 'text-sky-800', borderR: 'border-r border-sky-200/80', colWidth: 'w-24' },
    { key: 'sda', label: 'SDA', bg: 'bg-violet-100', cellBg: 'bg-violet-50/90', text: 'text-violet-800', borderR: 'border-r border-violet-200/80', colWidth: 'w-20' },
    { key: 'bee', label: 'BEE', bg: 'bg-teal-100', cellBg: 'bg-teal-50/90', text: 'text-teal-800', borderR: 'border-r border-teal-200/80', colWidth: 'w-20' },
  ];

  // Parse DB value like "R/A", "C/I", "R" into array of letters ['R','A'] etc.
  const parseRaciString = (value) => {
    if (!value || typeof value !== 'string') return [];
    return value
      .split(/[/,\s]+/)
      .map((s) => s.trim().toUpperCase())
      .filter((s) => ['R', 'A', 'C', 'I'].includes(s));
  };

  // RACI legend colors and descriptions (match legend design)
  const RACI_LEGEND = [
    { letter: 'R', name: 'Responsible', desc: 'The people who actually do the work. One or more people can be responsible.', bg: 'bg-emerald-200', text: 'text-emerald-900' },
    { letter: 'A', name: 'Accountable', desc: 'The Approver. The person who signs off on the work. Usually, only one person is accountable.', bg: 'bg-sky-200', text: 'text-sky-900' },
    { letter: 'C', name: 'Consulted', desc: 'The subject matter experts who are consulted and sometimes contribute to creating content.', bg: 'bg-violet-200', text: 'text-violet-900' },
    { letter: 'I', name: 'Informed', desc: 'The people who are informed when a deliverable is complete. Communication only goes one way.', bg: 'bg-rose-200', text: 'text-rose-900' },
  ];
  const roleStyle = (letter) => RACI_LEGEND.find((r) => r.letter === letter) || { bg: 'bg-gray-200', text: 'text-gray-800' };

  const renderStakeholderCell = (row, sh) => {
    const raciValue = row[sh.key] || '';
    const roles = parseRaciString(raciValue);
    const display = roles.length ? roles.join('/') : '–';
    return (
      <td key={sh.key} className={`py-3 px-2 text-gray-800 font-medium text-center align-middle ${sh.colWidth} ${sh.cellBg} ${sh.borderR}`}>
        {display}
      </td>
    );
  };

  return (
    <div className="p-6">
      <div className="bg-blue-600 rounded-xl p-6 shadow-sm mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-white/20 rounded-lg">
            <ClipboardList className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">
            RACI Matrix
          </h1>
        </div>
      </div>

      {/* Legend: stakeholders + RACI roles */}
      <div className="mb-6 rounded-2xl border border-gray-200/80 bg-gradient-to-br from-white to-slate-50/70 p-6 shadow-md shadow-gray-200/50">
        <div className="border-b border-slate-200/80 pb-5 mb-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Stakeholders</h3>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sky-100 text-sky-800 text-sm font-medium">ECSBC Cell</span>
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-violet-100 text-violet-800 text-sm font-medium">SDA</span>
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-teal-100 text-teal-800 text-sm font-medium">BEE</span>
          </div>
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">RACI roles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {RACI_LEGEND.map((r) => (
              <div key={r.letter} className="flex items-start gap-3 p-3 rounded-xl bg-white/80 border border-gray-100 shadow-sm hover:shadow transition-shadow">
                <span className={`shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg font-bold text-base shadow-sm ${r.bg} ${r.text}`}>
                  {r.letter}
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 text-sm leading-tight">{r.name}</p>
                  <p className="text-gray-500 text-xs leading-snug mt-0.5">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th className="py-3.5 px-4 font-bold text-gray-800 text-left border-b-2 border-r border-slate-200 bg-slate-100">Task</th>
                <th className="py-3.5 px-4 font-bold text-gray-800 text-left border-b-2 border-r border-slate-200 bg-slate-100">Subtask</th>
                {STAKEHOLDERS.map((sh) => (
                  <th key={sh.key} className={`py-3.5 px-2 font-bold text-center border-b-2 ${sh.colWidth} ${sh.borderR} ${sh.bg} ${sh.text}`}>
                    {sh.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={2 + STAKEHOLDERS.length} className="py-12 text-center text-gray-500 border border-gray-200 bg-gray-50/50">
                    No tasks or subtasks found.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`border-b border-gray-200 transition-colors hover:bg-gray-50/50 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                  >
                    {row.isFirstInTask ? (
                      <td
                        rowSpan={row.taskRowSpan}
                        className="py-3 px-4 align-top font-medium text-gray-800 w-[22%] bg-slate-50/60 border-r border-slate-200"
                      >
                        <span className="text-primary-600 font-semibold">Task {row.taskNo}</span>
                        <span className="text-gray-700 block mt-1">{row.taskDesc}</span>
                      </td>
                    ) : null}
                    <td className="py-3 px-4 text-gray-700 w-[28%] border-r border-slate-200">
                      <span className="font-medium text-gray-800">{row.subtaskNo}:</span> {row.subtaskDesc}
                    </td>
                    {STAKEHOLDERS.map((sh) => renderStakeholderCell(row, sh))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RaciMatrix;
